import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import nookies, { parseCookies } from 'nookies'

import { trackGoogleConversion } from 'features/tracking/services/googleConversionTrackingApi'

const autoSetParamsPath = ['/space/[...slug]', '/spaces/[...id]', '/pudo/[id]', '/locker/[id]']

export const useConversionTracking = (spaceId: string) => {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)

  const trackConversion = useCallback(async () => {
    setIsLoading(true)

    try {
      const rwgToken = parseCookies()[`rwg_token_${spaceId}`]

      if (!rwgToken) return

      const result = await trackGoogleConversion(rwgToken)

      if (result.isOk) {
        nookies.destroy(null, `rwg_token_${spaceId}`, {
          path: '/',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [spaceId])

  const saveRwgToken = useCallback(() => {
    if (!spaceId) return

    if (!autoSetParamsPath.includes(router.pathname)) return

    let rwgToken = router.query?.rwg_token

    const savedToken = parseCookies()[`rwg_token_${spaceId}`]

    if (rwgToken && (!savedToken || savedToken !== rwgToken)) {
      rwgToken = Array.isArray(rwgToken) ? rwgToken[0] : rwgToken

      nookies.set(null, `rwg_token_${spaceId}`, rwgToken, {
        maxAge: 60 * 60 * 24 * 30,
        path: '/',
        secure: true,
        sameSite: 'lax',
      })
    }
  }, [router.query, router.pathname, spaceId])

  useEffect(() => {
    if (!spaceId) return
    if (!autoSetParamsPath.includes(router.pathname)) return

    const savedToken = parseCookies()[`rwg_token_${spaceId}`]
    const rwgToken = router.query?.rwg_token

    if (savedToken && !rwgToken) {
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, rwg_token: savedToken },
      })
    }
  }, [spaceId, router, router.query, router.pathname])

  return {
    isLoading,
    trackConversion,
    saveRwgToken,
  }
}
